import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { httpsCallable } from "firebase/functions";
import {
  ArrowsClockwise,
  Barbell,
  CheckCircle,
  ForkKnife,
  ListChecks,
  NotePencil,
  PaperPlaneTilt,
  PencilSimple,
  Ruler,
  ShieldCheck,
  Trash,
  Warning,
} from "@phosphor-icons/react";
import ContentLoader from "../../components/shared/ContentLoader";
import {
  Line,
  LineChart,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";
import { functions } from "../../firebase";
import { useNoIndex } from "../../hooks/usePageSeo";
import { useToast } from "../../contexts/ToastContext";
import ConfirmDialog from "../../components/ui/ConfirmDialog";
import PoweredByCliniKit from "../../components/public/PoweredByCliniKit";
import MacroIcon from "../../components/nutrition/MacroIcon";
import {
  calcBlockNutrition,
  calcGoalProgress,
  calcIngredientNutrition,
  calcSubBlockNutrition,
} from "../../utils/nutritionCalc";
import "./MyMenuPage.css";

/**
 * MyMenuPage — the public page a nutrition client opens from an SMS.
 *
 * Compliance rule (Israeli MoH): this page NEVER touches Firestore. Every read
 * and write goes through unauthenticated Cloud Functions (App Check enforced),
 * so the callables stay local to this file and no helper is added to firebase.js.
 *
 * Identity is proven once with the last 4 digits of the phone; the server hands
 * back a signed session token valid for 5 days, kept in localStorage per link.
 */

// ============================================================
// STRINGS — every user-facing word, selected once per render.
// Hebrew is the default; English only when the server says so.
// ============================================================
const STRINGS = {
  he: {
    dir: "rtl",
    locale: "he-IL",

    loading: "טוען את הנתונים שלך...",
    verifying: "מאמת...",
    sending: "שולח...",
    errorTitle: "אופס",

    // Errors
    errLinkNotFound: "הקישור לא נמצא או שאינו פעיל יותר. בקש/י קישור חדש מהמלווה שלך.",
    errSessionInvalid: "פג תוקף ההתחברות. יש לאמת את הזהות מחדש.",
    errTooManyAttempts: "יותר מדי ניסיונות. נסה/י שוב בעוד שעה או פנה/י למלווה שלך.",
    errFoodLogDisabled: "דיווח האכילה אינו פעיל עבורך כרגע.",
    errMeasurementsDisabled: "דיווח המדידות אינו פעיל עבורך כרגע.",
    errTooManyEntries: "הגעת למספר הדיווחים המרבי להיום.",
    errEntryGone: "הדיווח כבר אינו קיים. רענן/י את הדף.",
    errInvalidInput: "הנתונים שהוזנו אינם תקינים.",
    errNetwork: "בעיית תקשורת. בדוק/י את החיבור לאינטרנט ונסה/י שוב.",
    errLoad: "אירעה שגיאה בטעינת הנתונים.",
    errVerify: "אירעה שגיאה באימות.",
    errSubmit: "אירעה שגיאה בשמירה.",
    errDelete: "אירעה שגיאה במחיקה.",

    // Verify
    verifyTitle: "אימות זהות",
    verifySubtitle: "להמשך, הזן/י את 4 הספרות האחרונות של מספר הטלפון שלך",
    verifyLabel: "4 הספרות האחרונות",
    verifyContinue: "המשך",
    verifyWrong: "המספר שהוזן שגוי",
    verifyAttemptsLeft: (n) => `המספר שהוזן שגוי. נותרו ${n} ניסיונות.`,

    // Header
    greeting: "שלום",
    todayLabel: "הדיווח של היום",

    // Totals
    calories: "קלוריות",
    protein: "חלבון",
    carbohydrates: "פחמימות",
    fat: "שומן",
    unitKcal: "קק״ל",
    unitGram: "גרם",
    unitKg: "ק״ג",
    ofGoal: "מתוך",
    noGoals: "ללא יעדים להצגה",

    // Tabs
    tabMenu: "התפריט שלי",
    tabFoodLog: "דיווח אכילה",
    tabMeasurements: "מדידות",
    nothingToShow: "אין כרגע תוכן להצגה",
    nothingToShowHint: "המלווה שלך יפעיל את הדיווח כשזה יהיה רלוונטי.",

    // Menu tab
    menuEmpty: "עדיין לא הוגדר עבורך תפריט",
    menuEmptyHint: "ברגע שהמלווה שלך יבנה תפריט, הוא יופיע כאן.",
    menuAverages: "תפריט ממוצעים",
    menuTotals: "סה״כ ליום",
    blockTotals: "סה״כ",
    altCounter: (n, total) => `${n} מתוך ${total}`,
    altNext: "הצגת החלופה הבאה",

    // Food log tab
    logTitle: "מה אכלת? פרט/י כמויות",
    logPlaceholder: "לדוגמה: 2 ביצים קשות, פרוסת לחם מלא עם כף טחינה וכוס קפה עם חלב",
    logHintAi: "ככל שתפרט/י כמויות, הערכת הקלוריות תהיה מדויקת יותר.",
    logHintPlain: "הדיווח נשמר כפי שנכתב והמלווה שלך יעבור עליו.",
    logComment: "הערה (לא חובה)",
    logCommentPlaceholder: "לדוגמה: החלפתי אורז לבן ב-50 גרם פסטה",
    logSubmit: "שליחת דיווח",
    logSubmitted: "הדיווח נשמר. תודה!",
    estimating: "מחשב ערכים...",
    estimateTitle: "בדוק/י את הפירוק לפני השליחה",
    estimateHint: "אלה הערכות בלבד. אפשר לתקן כל כמות או ערך לפני השמירה.",
    estimateConfirm: "אישור ושליחה",
    estimateDiscard: "שליחה ללא פירוק",
    estimateNeedsDetail: "אפשר לפרט קצת יותר? למשל הכמות או הגודל.",
    errEstimateThrottled: "ביצעת יותר מדי בקשות. נסה/י שוב בעוד מספר דקות.",
    logEmptyText: "יש לכתוב מה אכלת",
    todayEntries: "הדיווחים של היום",
    noEntries: "עדיין לא דיווחת היום",
    entryByCoach: "נרשם על ידי המלווה",
    edit: "עריכה",
    remove: "הסרה",
    save: "שמירה",
    cancel: "ביטול",
    saved: "הדיווח עודכן",
    removed: "הדיווח הוסר",
    deleteTitle: "הסרת דיווח",
    deleteMessage: "להסיר את הדיווח הזה? המלווה שלך עדיין יראה שהוסר.",
    deleteConfirm: "הסרה",
    foodName: "מזון",
    foodAmount: "כמות",

    // Measurements tab
    measureTitle: "הוספת מדידה",
    measureHint: "כל השדות אינם חובה — מספיק למלא שדה אחד.",
    measureSubmit: "שמירת מדידה",
    measureSaved: "המדידה נשמרה",
    measureEmpty: "יש למלא לפחות שדה אחד",
    measureHistory: "מדידות קודמות",
    noMeasurements: "עדיין אין מדידות",
    measureBySelf: "דיווח עצמי",
    measureByCoach: "נמדד במפגש",
    chartWeight: "משקל",
    chartBmi: "BMI",
    fields: {
      weight: "משקל (ק״ג)",
      height: "גובה (ס״מ)",
      fatPercentage: "אחוז שומן",
      neck: "היקף צוואר (ס״מ)",
      bust: "היקף חזה (ס״מ)",
      arm: "היקף זרוע (ס״מ)",
      waist: "היקף מותן (ס״מ)",
      hip: "היקף ירכיים (ס״מ)",
      thigh: "היקף רגל (ס״מ)",
    },
    bmi: "BMI",
  },
  en: {
    dir: "ltr",
    locale: "en-GB",

    loading: "Loading your data...",
    verifying: "Verifying...",
    sending: "Sending...",
    errorTitle: "Oops",

    errLinkNotFound: "This link was not found or is no longer active. Ask your coach for a new one.",
    errSessionInvalid: "Your session expired. Please verify your identity again.",
    errTooManyAttempts: "Too many attempts. Try again in an hour or contact your coach.",
    errFoodLogDisabled: "Food logging is currently turned off for you.",
    errMeasurementsDisabled: "Measurement logging is currently turned off for you.",
    errTooManyEntries: "You have reached today's maximum number of entries.",
    errEntryGone: "This entry no longer exists. Please refresh the page.",
    errInvalidInput: "The values entered are not valid.",
    errNetwork: "Connection problem. Check your internet and try again.",
    errLoad: "Something went wrong while loading your data.",
    errVerify: "Something went wrong during verification.",
    errSubmit: "Something went wrong while saving.",
    errDelete: "Something went wrong while removing.",

    verifyTitle: "Identity verification",
    verifySubtitle: "To continue, enter the last 4 digits of your phone number",
    verifyLabel: "Last 4 digits",
    verifyContinue: "Continue",
    verifyWrong: "The digits entered are incorrect",
    verifyAttemptsLeft: (n) => `Incorrect digits. ${n} attempts left.`,

    greeting: "Hello",
    todayLabel: "Today's log",

    calories: "Calories",
    protein: "Protein",
    carbohydrates: "Carbs",
    fat: "Fat",
    unitKcal: "kcal",
    unitGram: "g",
    unitKg: "kg",
    ofGoal: "of",
    noGoals: "No goals to show",

    tabMenu: "My menu",
    tabFoodLog: "Food log",
    tabMeasurements: "Measurements",
    nothingToShow: "Nothing to show right now",
    nothingToShowHint: "Your coach will turn on logging when it becomes relevant.",

    menuEmpty: "No menu has been set for you yet",
    menuEmptyHint: "As soon as your coach builds a menu, it will appear here.",
    menuAverages: "Averages menu",
    menuTotals: "Daily total",
    blockTotals: "Total",
    altCounter: (n, total) => `${n} of ${total}`,
    altNext: "Show the next alternative",

    logTitle: "What did you eat? Include amounts",
    logPlaceholder: "For example: 2 boiled eggs, a slice of whole wheat bread with a tablespoon of tahini and a coffee with milk",
    logHintAi: "The more detail you give about amounts, the more accurate the calorie estimate.",
    logHintPlain: "Your entry is saved as written and your coach will review it.",
    logComment: "Comment (optional)",
    logCommentPlaceholder: "e.g. I swapped the white rice for 50 g of pasta",
    logSubmit: "Send entry",
    logSubmitted: "Your entry was saved. Thank you!",
    estimating: "Calculating...",
    estimateTitle: "Check the breakdown before sending",
    estimateHint: "These are estimates only. You can correct any amount or value before saving.",
    estimateConfirm: "Confirm and send",
    estimateDiscard: "Send without a breakdown",
    estimateNeedsDetail: "Could you add a little more detail? For example the amount or size.",
    errEstimateThrottled: "Too many requests. Please try again in a few minutes.",
    logEmptyText: "Please describe what you ate",
    todayEntries: "Today's entries",
    noEntries: "You haven't logged anything today",
    entryByCoach: "Added by your coach",
    edit: "Edit",
    remove: "Remove",
    save: "Save",
    cancel: "Cancel",
    saved: "Entry updated",
    removed: "Entry removed",
    deleteTitle: "Remove entry",
    deleteMessage: "Remove this entry? Your coach will still see that it was removed.",
    deleteConfirm: "Remove",
    foodName: "Food",
    foodAmount: "Amount",

    measureTitle: "Add a measurement",
    measureHint: "All fields are optional — filling in one is enough.",
    measureSubmit: "Save measurement",
    measureSaved: "Measurement saved",
    measureEmpty: "Please fill in at least one field",
    measureHistory: "Previous measurements",
    noMeasurements: "No measurements yet",
    measureBySelf: "Self reported",
    measureByCoach: "Measured in session",
    chartWeight: "Weight",
    chartBmi: "BMI",
    fields: {
      weight: "Weight (kg)",
      height: "Height (cm)",
      fatPercentage: "Body fat %",
      neck: "Neck (cm)",
      bust: "Chest (cm)",
      arm: "Arm (cm)",
      waist: "Waist (cm)",
      hip: "Hips (cm)",
      thigh: "Thigh (cm)",
    },
    bmi: "BMI",
  },
};

const STAGES = {
  LOADING: "loading",
  VERIFY: "verify",
  READY: "ready",
  ERROR: "error",
};

const TABS = { MENU: "menu", FOOD_LOG: "foodLog", MEASUREMENTS: "measurements" };

const MACROS = ["calories", "protein", "carbohydrates", "fat"];

/** Mirrors the server ranges in nutritionPublicHelper.submitMeasurementPublic. */
const MEASUREMENT_FIELDS = [
  "weight",
  "height",
  "fatPercentage",
  "neck",
  "bust",
  "arm",
  "waist",
  "hip",
  "thigh",
];

const MAX_RAW_TEXT = 400;
const MAX_COMMENT = 500;

/* Progress rings: geometry shared by the SVG and the dash math. */
const RING_SIZE = 60;
const RING_STROKE = 4;
const RING_RADIUS = (RING_SIZE - RING_STROKE) / 2;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

/* Recharts paints strokes into SVG attributes, where a CSS variable is not
   guaranteed to resolve on every browser; the value is `--color-primary-dark`
   from src/styles/variables.css, copied verbatim. */
const CHART_STROKE = "#3cc4a1";
/* `--color-text-secondary`, same reason. */
const CHART_TICK = "#6b7280";
const CHART_HEIGHT = 160;

/** Recharts animations honor the OS "reduce motion" preference. */
const chartAnimationsEnabled = () =>
  !(
    typeof window !== "undefined" &&
    window.matchMedia?.("(prefers-reduced-motion: reduce)").matches
  );

// ============================================================
// Callables — the only channel this page has to the data.
// ============================================================
const callPublic = async (name, payload) => {
  const result = await httpsCallable(functions, name)(payload);
  return result.data;
};

// ============================================================
// Session token — per link, 5-day server-signed. localStorage
// throws in private mode, so every access is guarded.
// ============================================================
const sessionKey = (linkId) => `ck_menu_session_${linkId}`;

const readStoredToken = (linkId) => {
  try {
    return window.localStorage.getItem(sessionKey(linkId)) || null;
  } catch {
    return null;
  }
};

const writeStoredToken = (linkId, token) => {
  try {
    window.localStorage.setItem(sessionKey(linkId), token);
  } catch {
    /* private mode — the session simply lives for this page view */
  }
};

const clearStoredToken = (linkId) => {
  try {
    window.localStorage.removeItem(sessionKey(linkId));
  } catch {
    /* nothing to clean up */
  }
};

/**
 * Cloud Functions answer with English codes ("LINK_NOT_FOUND"); a client must
 * never see one. Every code is mapped to the page's language here.
 */
const mapPublicError = (error, s, fallback) => {
  const code = String(error?.code || "").replace(/^functions\//, "");
  const message = String(error?.message || "");
  const is = (needle) => message.includes(needle);

  if (is("SESSION_INVALID")) return s.errSessionInvalid;
  if (is("TOO_MANY_ATTEMPTS")) return s.errTooManyAttempts;
  if (is("TOO_MANY_ENTRIES")) return s.errTooManyEntries;
  if (is("FOOD_LOG_DISABLED")) return s.errFoodLogDisabled;
  if (is("MEASUREMENTS_DISABLED")) return s.errMeasurementsDisabled;
  if (is("ENTRY_NOT_FOUND") || is("ENTRY_DELETED")) return s.errEntryGone;
  if (is("LINK_NOT_FOUND")) return s.errLinkNotFound;
  if (is("NO_VALUES") || is("EMPTY_ENTRY") || is("NOTHING_TO_UPDATE")) {
    return s.errInvalidInput;
  }
  if (code === "not-found") return s.errLinkNotFound;
  if (code === "invalid-argument") return s.errInvalidInput;
  if (code === "unavailable" || code === "deadline-exceeded" || /network/i.test(message)) {
    return s.errNetwork;
  }
  return fallback;
};

const isSessionInvalid = (error) =>
  String(error?.code || "").replace(/^functions\//, "") === "permission-denied" &&
  String(error?.message || "").includes("SESSION_INVALID");

const toNum = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
};

const round = (value) => Math.round(toNum(value));

const formatDate = (ms, locale) =>
  ms
    ? new Intl.DateTimeFormat(locale, {
        timeZone: "Asia/Jerusalem",
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      }).format(new Date(ms))
    : "";

/** "YYYY-MM-DD" (the server's Israel day key) → a localized date. */
const formatDayKey = (dayKey, locale) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(dayKey || ""))) return "";
  const [year, month, day] = dayKey.split("-").map(Number);
  return new Intl.DateTimeFormat(locale, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(new Date(year, month - 1, day));
};

/** dd/MM for chart axis ticks — short on purpose, phone width. */
const formatShortDate = (ms) =>
  ms
    ? new Intl.DateTimeFormat("en-GB", {
        timeZone: "Asia/Jerusalem",
        day: "2-digit",
        month: "2-digit",
      }).format(new Date(ms))
    : "";

const formatTime = (ms, locale) =>
  ms
    ? new Intl.DateTimeFormat(locale, {
        timeZone: "Asia/Jerusalem",
        hour: "2-digit",
        minute: "2-digit",
      }).format(new Date(ms))
    : "";

// ============================================================
// Presentational pieces
// ============================================================

/**
 * Calories + 3 macros as circular progress rings against the goals.
 * Without a goal (none set, or hidden by the coach) the ring is a full
 * neutral circle and only the value shows — no percent, no "0/0".
 */
function TotalsBar({ totals, goals, s }) {
  return (
    <div className="nmp-totals" aria-label={s.todayLabel}>
      <div className="nmp-totals-head">
        <span className="nmp-totals-title">{s.todayLabel}</span>
        {!goals && <span className="nmp-totals-note">{s.noGoals}</span>}
      </div>
      <div className="nmp-rings">
        {MACROS.map((macro) => {
          const actual = round(totals?.[macro]);
          const goal = goals ? toNum(goals[macro]) : 0;
          const progress = calcGoalProgress(actual, goal);
          const unit = macro === "calories" ? s.unitKcal : s.unitGram;
          const filled = progress.hasGoal ? progress.barPercent : 100;
          const dashOffset = RING_CIRCUMFERENCE * (1 - filled / 100);

          return (
            <div key={macro} className={`nmp-ring-tile nmp-goal-${progress.status}`}>
              <div className="nmp-ring" role="presentation">
                <svg
                  className="nmp-ring-svg"
                  width={RING_SIZE}
                  height={RING_SIZE}
                  viewBox={`0 0 ${RING_SIZE} ${RING_SIZE}`}
                  aria-hidden="true"
                >
                  <circle
                    className="nmp-ring-track"
                    cx={RING_SIZE / 2}
                    cy={RING_SIZE / 2}
                    r={RING_RADIUS}
                    strokeWidth={RING_STROKE}
                  />
                  <circle
                    className="nmp-ring-fill"
                    cx={RING_SIZE / 2}
                    cy={RING_SIZE / 2}
                    r={RING_RADIUS}
                    strokeWidth={RING_STROKE}
                    strokeDasharray={RING_CIRCUMFERENCE}
                    strokeDashoffset={dashOffset}
                  />
                </svg>
                <MacroIcon macro={macro} size={20} className="nmp-ring-icon" />
              </div>
              <span className="nmp-ring-value">
                {actual}
                <span className="nmp-ring-unit">{unit}</span>
              </span>
              {progress.hasGoal && (
                <span className="nmp-ring-goal">
                  {s.ofGoal} {round(goal)} · {progress.percent}%
                </span>
              )}
              <span className="nmp-ring-label">{s[macro]}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Minimal trend line for one measurement field. Rendered only when the
 * caller has 2+ points — below that there is nothing to draw, so nothing
 * (not even an empty frame) is shown.
 */
function MeasurementChart({ rows, title, unit }) {
  // Measured by hand instead of <ResponsiveContainer>: that wrapper renders
  // nothing until its own ResizeObserver reports a width, and inside this
  // sticky/tabbed layout it was left blank. A plain observer on our own box
  // is deterministic — the chart draws as soon as the box has a width.
  const boxRef = useRef(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const node = boxRef.current;
    if (!node) return undefined;
    const measure = () => setWidth(Math.floor(node.getBoundingClientRect().width));
    measure();
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", measure);
      return () => window.removeEventListener("resize", measure);
    }
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const labelFor = (index) => rows[index]?.label ?? "";

  return (
    <div className="nmp-chart-card">
      <span className="nmp-chart-title">{title}</span>
      {/* LTR so the axes are not mirrored under the page's RTL direction. */}
      <div className="nmp-chart" dir="ltr" ref={boxRef}>
        {width > 0 && (
          <LineChart
            width={width}
            height={CHART_HEIGHT}
            data={rows}
            margin={{ top: 8, right: 12, left: 0, bottom: 0 }}
          >
            {/* x is the row index: two measurements on the same day must not
                collapse into one category (which hides the line entirely). */}
            <XAxis
              dataKey="x"
              tickFormatter={labelFor}
              tickLine={false}
              axisLine={false}
              tick={{ fontSize: 10, fill: CHART_TICK }}
            />
            <YAxis
              width={36}
              tickLine={false}
              axisLine={false}
              domain={["auto", "auto"]}
              tick={{ fontSize: 10, fill: CHART_TICK }}
            />
            <RechartsTooltip
              cursor={{ stroke: "var(--color-border)" }}
              labelFormatter={labelFor}
              formatter={(value) => [`${value}${unit ? ` ${unit}` : ""}`, title]}
              contentStyle={{
                fontSize: "0.72rem",
                borderRadius: "var(--radius-md)",
                border: "1px solid var(--color-border-light)",
                boxShadow: "var(--shadow-md)",
              }}
            />
            <Line
              type="monotone"
              dataKey="value"
              name={title}
              stroke={CHART_STROKE}
              strokeWidth={2}
              dot={{ r: 3, fill: CHART_STROKE, strokeWidth: 0 }}
              activeDot={{ r: 5 }}
              isAnimationActive={chartAnimationsEnabled()}
              animationDuration={400}
            />
          </LineChart>
        )}
      </div>
    </div>
  );
}

/** Oldest → newest rows of one numeric field; [] unless there are 2+ points. */
const chartRowsFor = (measurements, field) => {
  const rows = measurements
    .filter((m) => m?.[field] != null && Number.isFinite(Number(m[field])))
    .map((m) => ({
      id: m.id,
      date: toNum(m.date),
      label: formatShortDate(m.date),
      value: Number(m[field]),
    }))
    .sort((a, b) => a.date - b.date)
    .map((row, x) => ({ ...row, x }));
  return rows.length >= 2 ? rows : [];
};

/** Four macro numbers in a compact row — used by menu blocks and log entries. */
function MacroLine({ nutrition, s }) {
  return (
    <div className="nmp-macro-line">
      <span className="nmp-macro nmp-macro-cal">
        {round(nutrition.calories)} {s.unitKcal}
      </span>
      <span className="nmp-macro">
        {s.protein} {round(nutrition.protein)}
      </span>
      <span className="nmp-macro">
        {s.carbohydrates} {round(nutrition.carbohydrates)}
      </span>
      <span className="nmp-macro">
        {s.fat} {round(nutrition.fat)}
      </span>
    </div>
  );
}

/**
 * Editable food rows.
 *
 * Used today by the entry edit panel, and it is also the surface the Phase 6 AI
 * estimate will review in (see the seam in handleSubmitEntry) — the client edits
 * the amounts the model guessed before confirming.
 */
function FoodsEditor({ foods, onChange, s }) {
  const patch = (index, key, value) => {
    onChange(foods.map((food, i) => (i === index ? { ...food, [key]: value } : food)));
  };

  return (
    <div className="nmp-foods-editor">
      {foods.map((food, index) => (
        <div className="nmp-food-row" key={`${food.name}-${index}`}>
          <span className="nmp-food-name">{food.name || s.foodName}</span>
          <div className="nmp-food-inputs">
            <label className="nmp-mini-field">
              <span>{s.foodAmount}</span>
              <input
                type="number"
                inputMode="decimal"
                className="nmp-num-input"
                value={food.amount ?? ""}
                onChange={(e) => patch(index, "amount", e.target.value)}
              />
            </label>
            {MACROS.map((macro) => (
              <label className="nmp-mini-field" key={macro}>
                <span>{s[macro]}</span>
                <input
                  type="number"
                  inputMode="decimal"
                  className="nmp-num-input"
                  value={food[macro] ?? ""}
                  onChange={(e) => patch(index, macro, e.target.value)}
                />
              </label>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * One food row. When the coach attached interchangeable foods, a single one
 * is visible at a time with a "1 מתוך 3" counter and slider dots; tapping the
 * row (or the rotate button) flips to the next. Single-food rows stay plain.
 */
function MenuIngredient({ ingredient, s }) {
  const options = [ingredient, ...(ingredient.connectedIngredients || [])];
  const total = options.length;
  const hasAlternatives = total > 1;

  const [shownIndex, setShownIndex] = useState(0);
  // Bumped on every swap so the flip keyframe restarts (key remount).
  const [flipKey, setFlipKey] = useState(0);

  const option = options[Math.min(shownIndex, total - 1)];

  const showNext = () => {
    if (!hasAlternatives) return;
    setShownIndex((index) => (index + 1) % total);
    setFlipKey((key) => key + 1);
  };

  const content = (
    <div className="nmp-option" key={flipKey}>
      <div className="nmp-option-main">
        <span className="nmp-option-name">{option.foodName}</span>
        <span className="nmp-option-amount">
          {option.amount} {option.selectedAmount}
        </span>
      </div>
      <MacroLine nutrition={calcIngredientNutrition(option)} s={s} />
    </div>
  );

  if (!hasAlternatives) {
    return <li className="nmp-ingredient">{content}</li>;
  }

  return (
    <li className="nmp-ingredient nmp-ingredient-alt">
      <div className="nmp-alt-row" onClick={showNext}>
        <div className="nmp-alt-head">
          <span className="nmp-alt-counter">{s.altCounter(shownIndex + 1, total)}</span>
          <button
            type="button"
            className="nmp-alt-btn"
            aria-label={s.altNext}
            title={s.altNext}
            onClick={(e) => {
              e.stopPropagation();
              showNext();
            }}
          >
            <ArrowsClockwise size={13} weight="bold" />
          </button>
        </div>
        <div className="nmp-alt-flip">{content}</div>
        <div className="nmp-alt-dots" aria-hidden="true">
          {options.map((_, index) => (
            <span
              key={index}
              className={`nmp-alt-dot${index === shownIndex ? " active" : ""}`}
            />
          ))}
        </div>
      </div>
    </li>
  );
}

/**
 * One menu section, titled with whatever the coach typed. An unnamed section
 * shows no heading text at all — never a placeholder. Always fully expanded:
 * on a phone the client wants to glance at the whole menu and close the page,
 * not tap section by section.
 */
function MenuBlock({ block, isAveragesMenu, s }) {
  const totals = calcBlockNutrition(block, isAveragesMenu);
  const blockName = String(block.blockName || "").trim();

  return (
    <section className="nmp-block">
      <div className="nmp-block-head">
        <span className="nmp-block-name">{blockName}</span>
        <span className="nmp-block-total">
          {round(totals.calories)} {s.unitKcal}
        </span>
      </div>

      <div className="nmp-block-body">
          {block.comment && <p className="nmp-note">{block.comment}</p>}

          {(block.subBlocks || []).map((subBlock, index) => {
            const subTotals = calcSubBlockNutrition(subBlock);
            const subName = String(subBlock.subBlockName || "").trim();
            return (
              <div className="nmp-subblock" key={`${subBlock.subBlockName}-${index}`}>
                {subName && (
                  <div className="nmp-subblock-head">
                    <span className="nmp-subblock-name">{subName}</span>
                    <span className="nmp-subblock-total">
                      {s.blockTotals} {round(subTotals.calories)} {s.unitKcal}
                    </span>
                  </div>
                )}
                <ul className="nmp-ingredients">
                  {(subBlock.ingredients || []).map((ingredient, i) => (
                    <MenuIngredient
                      key={`${ingredient.foodName}-${i}`}
                      ingredient={ingredient}
                      s={s}
                    />
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
    </section>
  );
}

// ============================================================
// Page
// ============================================================
function MyMenuPage() {
  const { linkId } = useParams();
  useNoIndex();
  const { showSuccess, showError } = useToast();

  const [stage, setStage] = useState(STAGES.LOADING);
  // The token also lives in a ref: localStorage throws in private mode, and the
  // session must still survive for the length of this page view.
  const tokenRef = useRef(null);
  const [language, setLanguage] = useState("he");
  const [errorMessage, setErrorMessage] = useState("");

  const [linkInfo, setLinkInfo] = useState(null);
  const [data, setData] = useState(null);
  const [activeTab, setActiveTab] = useState(TABS.MENU);

  const [digits, setDigits] = useState("");
  const [verifyError, setVerifyError] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  // Display-only: tells a returning client why they are asked to verify again.
  const [sessionLapsed, setSessionLapsed] = useState(false);


  const [rawText, setRawText] = useState("");
  const [entryComment, setEntryComment] = useState("");
  const [isSubmittingEntry, setIsSubmittingEntry] = useState(false);
  const [logError, setLogError] = useState("");
  // AI estimate review: `estimate` is the breakdown awaiting the client's
  // approval (null = nothing to review yet), `estimateMeta` is what gets
  // stored alongside the entry for the coach's audit trail.
  const [estimate, setEstimate] = useState(null);
  const [estimateMeta, setEstimateMeta] = useState(null);
  const [estimateQuestion, setEstimateQuestion] = useState("");

  const [editingId, setEditingId] = useState(null);
  const [editFoods, setEditFoods] = useState([]);
  const [editComment, setEditComment] = useState("");
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);

  const [measureValues, setMeasureValues] = useState({});
  const [isSavingMeasurement, setIsSavingMeasurement] = useState(false);
  const [measureError, setMeasureError] = useState("");

  const s = useMemo(() => STRINGS[language] || STRINGS.he, [language]);

  /** Load everything behind the session token. Falls back to verify if it lapsed. */
  const loadData = useCallback(
    async (token) => {
      try {
        const result = await callPublic("getMenuDataPublic", { linkId, sessionToken: token });
        setData(result);
        if (result.language) setLanguage(result.language === "en" ? "en" : "he");
        setStage(STAGES.READY);
        return true;
      } catch (error) {
        if (isSessionInvalid(error)) {
          tokenRef.current = null;
          clearStoredToken(linkId);
          setSessionLapsed(true);
          setStage(STAGES.VERIFY);
          return false;
        }
        console.error("[CK-6202] Failed to load public menu data:", {
          errorCode: "CK-6202",
          errorMessage: error.message,
          linkId,
          timestamp: new Date().toISOString(),
        });
        setErrorMessage(mapPublicError(error, STRINGS[language] || STRINGS.he, (STRINGS[language] || STRINGS.he).errLoad));
        setStage(STAGES.ERROR);
        return false;
      }
    },
    [linkId, language]
  );

  // First load: link metadata (language + names), then either the stored
  // session or the verification stage.
  useEffect(() => {
    let cancelled = false;

    const bootstrap = async () => {
      try {
        const info = await callPublic("getMenuLinkPublic", { linkId });
        if (cancelled) return;

        const lang = info.language === "en" ? "en" : "he";
        setLanguage(lang);
        setLinkInfo(info);

        if (info.isLocked) {
          setErrorMessage(STRINGS[lang].errTooManyAttempts);
          setStage(STAGES.ERROR);
          return;
        }

        const token = readStoredToken(linkId);
        if (token) {
          tokenRef.current = token;
          await loadData(token);
          return;
        }
        setStage(STAGES.VERIFY);
      } catch (error) {
        if (cancelled) return;
        console.error("[CK-6200] Failed to load a public menu link:", {
          errorCode: "CK-6200",
          errorMessage: error.message,
          linkId,
          timestamp: new Date().toISOString(),
        });
        setErrorMessage(mapPublicError(error, STRINGS.he, STRINGS.he.errLoad));
        setStage(STAGES.ERROR);
      }
    };

    bootstrap();
    return () => {
      cancelled = true;
    };
    // loadData is intentionally excluded: it depends on `language`, which this
    // effect sets — re-running would refetch the link on every language change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkId]);

  // Land on the first tab the client actually has.
  useEffect(() => {
    if (!data?.tabs) return;
    setActiveTab((current) => {
      if (data.tabs[current]) return current;
      if (data.tabs.menu) return TABS.MENU;
      if (data.tabs.foodLog) return TABS.FOOD_LOG;
      return TABS.MEASUREMENTS;
    });
  }, [data]);

  const sessionToken = () => tokenRef.current || readStoredToken(linkId);

  const refresh = async () => {
    const token = sessionToken();
    if (token) await loadData(token);
  };

  const handleVerify = async () => {
    if (digits.length !== 4 || isVerifying) return;
    setVerifyError("");
    setIsVerifying(true);

    try {
      const result = await callPublic("verifyMenuIdentity", {
        linkId,
        lastFourDigits: digits,
      });

      if (!result.success) {
        setVerifyError(
          typeof result.attemptsLeft === "number"
            ? s.verifyAttemptsLeft(result.attemptsLeft)
            : s.verifyWrong
        );
        setDigits("");
        return;
      }

      tokenRef.current = result.sessionToken;
      writeStoredToken(linkId, result.sessionToken);
      setStage(STAGES.LOADING);
      await loadData(result.sessionToken);
    } catch (error) {
      console.error("[CK-6201] Menu identity verification failed:", {
        errorCode: "CK-6201",
        errorMessage: error.message,
        linkId,
        timestamp: new Date().toISOString(),
      });
      const message = mapPublicError(error, s, s.errVerify);
      if (message === s.errTooManyAttempts || message === s.errLinkNotFound) {
        setErrorMessage(message);
        setStage(STAGES.ERROR);
        return;
      }
      setVerifyError(message);
    } finally {
      setIsVerifying(false);
    }
  };

  /** Write the entry, optionally with a breakdown the client already approved. */
  const saveEntry = async (text, foods) => {
    await callPublic("submitFoodEntry", {
      linkId,
      sessionToken: sessionToken(),
      rawText: text,
      comment: entryComment.trim(),
      foods,
      ...(estimateMeta ? { ai: estimateMeta } : {}),
    });

    setRawText("");
    setEntryComment("");
    setEstimate(null);
    setEstimateMeta(null);
    setEstimateQuestion("");
    showSuccess(s.logSubmitted);
    await refresh();
  };

  /**
   * Step 1 of logging when the coach enabled AI: ask the server to break the
   * text into foods, then show them for correction. NOTHING is stored yet —
   * an AI guess only becomes a record once the client confirms it.
   *
   * If the estimate is unavailable for any reason (throttled, disabled, the
   * model is down), fall back to saving the text as written rather than
   * blocking the client from logging their meal at all.
   */
  const handleEstimate = async (text) => {
    try {
      const result = await callPublic("estimateFoodEntry", {
        linkId,
        sessionToken: sessionToken(),
        rawText: text,
      });

      if (result?.needsDetail) {
        setEstimateQuestion(result.question || s.estimateNeedsDetail);
        return true;
      }

      setEstimate(result?.foods || []);
      setEstimateQuestion("");
      setEstimateMeta(
        result?.source === "ai"
          ? { model: result.model, tokensUsed: result.tokensUsed, needsDetail: false }
          : null
      );
      return true;
    } catch (error) {
      console.error("[CK-6207] Food estimate failed:", {
        errorCode: "CK-6207",
        errorMessage: error.message,
        linkId,
        timestamp: new Date().toISOString(),
      });

      const code = String(error?.message || "");
      // A throttle is worth telling the client about; anything else just
      // silently degrades to logging the text as-is.
      if (code.includes("THROTTLED") || code.includes("DAILY_CAP")) {
        showError(s.errEstimateThrottled);
        return false;
      }
      await saveEntry(text, []);
      return false;
    }
  };

  const handleSubmitEntry = async () => {
    const text = rawText.trim();
    if (!text) {
      setLogError(s.logEmptyText);
      return;
    }
    setLogError("");
    setIsSubmittingEntry(true);

    try {
      // With AI on, the first press estimates and shows the breakdown for
      // review; the second press (once `estimate` exists) saves it.
      if (data?.aiEstimateEnabled && estimate === null) {
        const handled = await handleEstimate(text);
        if (handled) return;
        return;
      }

      await saveEntry(text, estimate || []);
    } catch (error) {
      console.error("[CK-6203] Failed to submit a food entry:", {
        errorCode: "CK-6203",
        errorMessage: error.message,
        linkId,
        timestamp: new Date().toISOString(),
      });
      showError(mapPublicError(error, s, s.errSubmit));
    } finally {
      setIsSubmittingEntry(false);
    }
  };

  const startEdit = (entry) => {
    setEditingId(entry.id);
    setEditFoods((entry.foods || []).map((food) => ({ ...food })));
    setEditComment(entry.comment || "");
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditFoods([]);
    setEditComment("");
  };

  const handleSaveEdit = async () => {
    if (!editingId || isSavingEdit) return;
    setIsSavingEdit(true);

    try {
      await callPublic("updateFoodEntryPublic", {
        linkId,
        sessionToken: sessionToken(),
        entryId: editingId,
        foods: editFoods.map((food) => ({
          name: food.name,
          amount: toNum(food.amount),
          unit: food.unit || "",
          calories: toNum(food.calories),
          protein: toNum(food.protein),
          carbohydrates: toNum(food.carbohydrates),
          fat: toNum(food.fat),
        })),
        comment: editComment.trim(),
      });

      cancelEdit();
      showSuccess(s.saved);
      await refresh();
    } catch (error) {
      console.error("[CK-6204] Failed to update a food entry:", {
        errorCode: "CK-6204",
        errorMessage: error.message,
        linkId,
        timestamp: new Date().toISOString(),
      });
      showError(mapPublicError(error, s, s.errSubmit));
    } finally {
      setIsSavingEdit(false);
    }
  };

  const handleDeleteEntry = async () => {
    const entryId = pendingDeleteId;
    if (!entryId) return;

    try {
      await callPublic("deleteFoodEntryPublic", {
        linkId,
        sessionToken: sessionToken(),
        entryId,
      });
      if (editingId === entryId) cancelEdit();
      showSuccess(s.removed);
      await refresh();
    } catch (error) {
      console.error("[CK-6205] Failed to remove a food entry:", {
        errorCode: "CK-6205",
        errorMessage: error.message,
        linkId,
        timestamp: new Date().toISOString(),
      });
      showError(mapPublicError(error, s, s.errDelete));
    } finally {
      setPendingDeleteId(null);
    }
  };

  const handleSubmitMeasurement = async () => {
    const payload = {};
    MEASUREMENT_FIELDS.forEach((field) => {
      const value = measureValues[field];
      if (value !== undefined && value !== null && String(value).trim() !== "") {
        payload[field] = Number(value);
      }
    });

    if (Object.keys(payload).length === 0) {
      setMeasureError(s.measureEmpty);
      return;
    }

    setMeasureError("");
    setIsSavingMeasurement(true);

    try {
      await callPublic("submitMeasurementPublic", {
        linkId,
        sessionToken: sessionToken(),
        ...payload,
      });
      setMeasureValues({});
      showSuccess(s.measureSaved);
      await refresh();
    } catch (error) {
      console.error("[CK-6206] Failed to submit a client measurement:", {
        errorCode: "CK-6206",
        errorMessage: error.message,
        linkId,
        timestamp: new Date().toISOString(),
      });
      showError(mapPublicError(error, s, s.errSubmit));
    } finally {
      setIsSavingMeasurement(false);
    }
  };

  // ============================================================
  // Stages
  // ============================================================
  if (stage === STAGES.LOADING) {
    return (
      <div className="nmp-page nmp-page-centered" dir={s.dir} lang={language}>
        <div className="nmp-state-card">
          <ContentLoader message={s.loading} />
        </div>
        <PoweredByCliniKit source="menu_page" />
      </div>
    );
  }

  if (stage === STAGES.ERROR) {
    return (
      <div className="nmp-page nmp-page-centered" dir={s.dir} lang={language}>
        <div className="nmp-state-card">
          <div className="nmp-state-icon nmp-state-icon-error">
            <Warning size={20} weight="fill" />
          </div>
          <h1>{s.errorTitle}</h1>
          <p>{errorMessage}</p>
        </div>
        <PoweredByCliniKit source="menu_page" />
      </div>
    );
  }

  if (stage === STAGES.VERIFY) {
    return (
      <div className="nmp-page nmp-page-centered" dir={s.dir} lang={language}>
        <div className="nmp-state-card">
          <div className="nmp-state-icon">
            <ShieldCheck size={20} weight="fill" />
          </div>
          <h1>{s.verifyTitle}</h1>
          {linkInfo?.patientFirstName && (
            <p className="nmp-state-greeting">
              {s.greeting} {linkInfo.patientFirstName}
            </p>
          )}
          <p>{s.verifySubtitle}</p>
          {sessionLapsed && <p className="nmp-state-note">{s.errSessionInvalid}</p>}

          <label className="nmp-field nmp-verify-field">
            <span className="nmp-field-label">{s.verifyLabel}</span>
            <input
              type="tel"
              inputMode="numeric"
              autoComplete="one-time-code"
              className={`nmp-digits-input${verifyError ? " invalid" : ""}`}
              value={digits}
              maxLength={4}
              onChange={(e) => setDigits(e.target.value.replace(/\D/g, "").slice(0, 4))}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleVerify();
              }}
            />
          </label>

          {verifyError && <p className="nmp-form-error">{verifyError}</p>}

          <button
            type="button"
            className="nmp-primary-btn nmp-full"
            onClick={handleVerify}
            disabled={digits.length !== 4 || isVerifying}
          >
            {isVerifying ? s.verifying : s.verifyContinue}
          </button>
        </div>
        <PoweredByCliniKit source="menu_page" />
      </div>
    );
  }

  // ============================================================
  // READY
  // ============================================================
  const tabs = data?.tabs || {};
  const entries = data?.entries || [];
  const measurements = data?.measurements || [];
  const menu = data?.menu || null;
  const weightRows = chartRowsFor(measurements, "weight");
  const bmiRows = chartRowsFor(measurements, "bmi");

  const tabDefs = [
    { key: TABS.MENU, label: s.tabMenu, Icon: ListChecks, enabled: tabs.menu },
    { key: TABS.FOOD_LOG, label: s.tabFoodLog, Icon: ForkKnife, enabled: tabs.foodLog },
    { key: TABS.MEASUREMENTS, label: s.tabMeasurements, Icon: Ruler, enabled: tabs.measurements },
  ].filter((tab) => tab.enabled);

  return (
    <div className="nmp-page" dir={s.dir} lang={language}>
      <header className="nmp-topbar">
        <div className="nmp-topbar-text">
          <span className="nmp-eyebrow">{linkInfo?.clinicName || ""}</span>
          <h1 className="nmp-title">
            {s.greeting} {data?.patientFirstName || linkInfo?.patientFirstName || ""}
          </h1>
        </div>
        <span className="nmp-date-badge">{formatDayKey(data?.date, s.locale)}</span>
      </header>

      <TotalsBar totals={data?.totals} goals={data?.goals || null} s={s} />

      {tabDefs.length > 1 && (
        <nav className="nmp-tabs" role="tablist" aria-label={s.tabMenu}>
          {tabDefs.map(({ key, label, Icon }) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={activeTab === key}
              className={`nmp-tab${activeTab === key ? " active" : ""}`}
              onClick={() => setActiveTab(key)}
            >
              <Icon size={15} weight={activeTab === key ? "fill" : "regular"} />
              <span>{label}</span>
            </button>
          ))}
        </nav>
      )}

      <main className="nmp-body">
        {/* ===== Nothing enabled — the coach turned every tab off ===== */}
        {tabDefs.length === 0 && (
          <section className="nmp-panel">
            <div className="nmp-empty">
              <ListChecks size={24} weight="fill" />
              <p>{s.nothingToShow}</p>
              <p className="nmp-empty-hint">{s.nothingToShowHint}</p>
            </div>
          </section>
        )}

        {/* ===== Menu ===== */}
        {activeTab === TABS.MENU && tabs.menu && (
          <section className="nmp-panel">
            {!menu ? (
              <div className="nmp-empty">
                <ForkKnife size={24} weight="fill" />
                <p>{s.menuEmpty}</p>
                <p className="nmp-empty-hint">{s.menuEmptyHint}</p>
              </div>
            ) : (
              <>
                <div className="nmp-menu-head">
                  <span className="nmp-menu-name">{menu.menuName}</span>
                  {menu.isAveragesMenu && (
                    <span className="nmp-badge nmp-badge-info">{s.menuAverages}</span>
                  )}
                </div>
                {menu.comment && <p className="nmp-note">{menu.comment}</p>}
                {menu.totals && (
                  <div className="nmp-menu-totals">
                    <span className="nmp-menu-totals-label">{s.menuTotals}</span>
                    <MacroLine nutrition={menu.totals} s={s} />
                  </div>
                )}

                {(menu.blocks || []).map((block, index) => (
                  <MenuBlock
                    key={`${block.blockName}-${index}`}
                    block={block}
                    isAveragesMenu={menu.isAveragesMenu}
                    s={s}
                  />
                ))}
              </>
            )}
          </section>
        )}

        {/* ===== Food log ===== */}
        {activeTab === TABS.FOOD_LOG && tabs.foodLog && (
          <section className="nmp-panel">
            <div className="nmp-card">
              <label className="nmp-field">
                <span className="nmp-field-label">{s.logTitle}</span>
                <textarea
                  className="nmp-textarea"
                  rows={4}
                  maxLength={MAX_RAW_TEXT}
                  value={rawText}
                  onChange={(e) => setRawText(e.target.value)}
                  placeholder={s.logPlaceholder}
                />
              </label>
              <p className="nmp-hint">
                {data?.aiEstimateEnabled ? s.logHintAi : s.logHintPlain}
              </p>

              <label className="nmp-field">
                <span className="nmp-field-label">{s.logComment}</span>
                <input
                  type="text"
                  className="nmp-text-input"
                  maxLength={MAX_COMMENT}
                  value={entryComment}
                  onChange={(e) => setEntryComment(e.target.value)}
                  placeholder={s.logCommentPlaceholder}
                />
              </label>

              {logError && <p className="nmp-form-error">{logError}</p>}

              {/* The model asked for more detail rather than guessing. */}
              {estimateQuestion && (
                <p className="nmp-estimate-question">{estimateQuestion}</p>
              )}

              {/* Review panel: the breakdown is editable, and nothing is
                  stored until the client presses confirm. */}
              {estimate !== null && (
                <div className="nmp-estimate">
                  <span className="nmp-estimate-title">{s.estimateTitle}</span>
                  <p className="nmp-hint">{s.estimateHint}</p>
                  <FoodsEditor foods={estimate} onChange={setEstimate} s={s} />
                  <button
                    type="button"
                    className="nmp-ghost-btn nmp-full"
                    onClick={() => {
                      setEstimate(null);
                      setEstimateMeta(null);
                      setEstimateQuestion("");
                    }}
                    disabled={isSubmittingEntry}
                  >
                    {s.estimateDiscard}
                  </button>
                </div>
              )}

              <button
                type="button"
                className="nmp-primary-btn nmp-full"
                onClick={handleSubmitEntry}
                disabled={isSubmittingEntry || !rawText.trim()}
              >
                <PaperPlaneTilt size={14} weight="fill" />
                <span>
                  {isSubmittingEntry
                    ? data?.aiEstimateEnabled && estimate === null
                      ? s.estimating
                      : s.sending
                    : estimate !== null
                      ? s.estimateConfirm
                      : s.logSubmit}
                </span>
              </button>
            </div>

            <h2 className="nmp-section-title">{s.todayEntries}</h2>

            {entries.length === 0 ? (
              <div className="nmp-empty">
                <NotePencil size={24} weight="fill" />
                <p>{s.noEntries}</p>
              </div>
            ) : (
              <ul className="nmp-entries">
                {entries.map((entry) => (
                  <li className="nmp-entry" key={entry.id}>
                    <div className="nmp-entry-head">
                      <span className="nmp-entry-time">
                        {formatTime(entry.at, s.locale)}
                      </span>
                      {entry.source === "coach" && (
                        <span className="nmp-badge nmp-badge-info">{s.entryByCoach}</span>
                      )}
                      {entry.source !== "coach" && (
                        <div className="nmp-entry-actions">
                          <button
                            type="button"
                            className="nmp-icon-btn"
                            onClick={() =>
                              editingId === entry.id ? cancelEdit() : startEdit(entry)
                            }
                            aria-label={s.edit}
                          >
                            <PencilSimple size={14} weight="fill" />
                          </button>
                          <button
                            type="button"
                            className="nmp-icon-btn danger"
                            onClick={() => setPendingDeleteId(entry.id)}
                            aria-label={s.remove}
                          >
                            <Trash size={14} weight="fill" />
                          </button>
                        </div>
                      )}
                    </div>

                    {entry.rawText && <p className="nmp-entry-text">{entry.rawText}</p>}
                    {entry.comment && <p className="nmp-note">{entry.comment}</p>}
                    {entry.totals && <MacroLine nutrition={entry.totals} s={s} />}

                    {editingId === entry.id && (
                      <div className="nmp-edit-panel">
                        {editFoods.length > 0 && (
                          <FoodsEditor foods={editFoods} onChange={setEditFoods} s={s} />
                        )}
                        <label className="nmp-field">
                          <span className="nmp-field-label">{s.logComment}</span>
                          <input
                            type="text"
                            className="nmp-text-input"
                            maxLength={MAX_COMMENT}
                            value={editComment}
                            onChange={(e) => setEditComment(e.target.value)}
                          />
                        </label>
                        <div className="nmp-edit-actions">
                          <button
                            type="button"
                            className="nmp-secondary-btn"
                            onClick={cancelEdit}
                          >
                            {s.cancel}
                          </button>
                          <button
                            type="button"
                            className="nmp-primary-btn"
                            onClick={handleSaveEdit}
                            disabled={isSavingEdit}
                          >
                            <CheckCircle size={14} weight="fill" />
                            <span>{isSavingEdit ? s.sending : s.save}</span>
                          </button>
                        </div>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}

        {/* ===== Measurements ===== */}
        {activeTab === TABS.MEASUREMENTS && tabs.measurements && (
          <section className="nmp-panel">
            <div className="nmp-card">
              <h2 className="nmp-section-title">{s.measureTitle}</h2>
              <p className="nmp-hint">{s.measureHint}</p>

              <div className="nmp-measure-grid">
                {MEASUREMENT_FIELDS.map((field) => (
                  <label className="nmp-field" key={field}>
                    <span className="nmp-field-label">{s.fields[field]}</span>
                    <input
                      type="number"
                      inputMode="decimal"
                      className="nmp-num-input"
                      value={measureValues[field] ?? ""}
                      onChange={(e) =>
                        setMeasureValues((prev) => ({ ...prev, [field]: e.target.value }))
                      }
                    />
                  </label>
                ))}
              </div>

              {measureError && <p className="nmp-form-error">{measureError}</p>}

              <button
                type="button"
                className="nmp-primary-btn nmp-full"
                onClick={handleSubmitMeasurement}
                disabled={isSavingMeasurement}
              >
                <Barbell size={14} weight="fill" />
                <span>{isSavingMeasurement ? s.sending : s.measureSubmit}</span>
              </button>
            </div>

            {(weightRows.length > 0 || bmiRows.length > 0) && (
              <div className="nmp-charts">
                {weightRows.length > 0 && (
                  <MeasurementChart rows={weightRows} title={s.chartWeight} unit={s.unitKg} />
                )}
                {bmiRows.length > 0 && (
                  <MeasurementChart rows={bmiRows} title={s.chartBmi} unit="" />
                )}
              </div>
            )}

            <h2 className="nmp-section-title">{s.measureHistory}</h2>

            {measurements.length === 0 ? (
              <div className="nmp-empty">
                <Ruler size={24} weight="fill" />
                <p>{s.noMeasurements}</p>
              </div>
            ) : (
              <ul className="nmp-measure-list">
                {measurements.map((measurement) => (
                  <li className="nmp-measure-row" key={measurement.id}>
                    <span className="nmp-measure-date">
                      {formatDate(measurement.date, s.locale)}
                    </span>
                    <div className="nmp-measure-values">
                      {measurement.weight != null && (
                        <span className="nmp-measure-value">
                          {measurement.weight} {s.unitKg}
                        </span>
                      )}
                      {measurement.fatPercentage != null && (
                        <span className="nmp-measure-value">
                          {measurement.fatPercentage}%
                        </span>
                      )}
                      {measurement.bmi != null && (
                        <span className="nmp-measure-value">
                          {s.bmi} {measurement.bmi}
                        </span>
                      )}
                    </div>
                    <span
                      className={`nmp-badge ${
                        measurement.source === "client"
                          ? "nmp-badge-self"
                          : "nmp-badge-info"
                      }`}
                    >
                      {measurement.source === "client" ? s.measureBySelf : s.measureByCoach}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}
      </main>

      <ConfirmDialog
        isOpen={Boolean(pendingDeleteId)}
        onClose={() => setPendingDeleteId(null)}
        onConfirm={handleDeleteEntry}
        title={s.deleteTitle}
        message={s.deleteMessage}
        confirmText={s.deleteConfirm}
        cancelText={s.cancel}
        type="danger"
        icon="delete"
      />

      <PoweredByCliniKit source="menu_page" />
    </div>
  );
}

export default MyMenuPage;
